import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DATE_FORMATS_FNS } from "components";
import { TYRETEAM_CHANNELS } from "models";
import { usePermanentTyreTeamDelivery } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { DealerLocation } from "models";

const deliveryChannels: Record<TYRETEAM_CHANNELS, string> = {
  [TYRETEAM_CHANNELS.WheelTyre]: "Wheel-Tyre",
  [TYRETEAM_CHANNELS.BandenExpress]: "Banden Express"
};

export type DeliveryPopup = {
  trigger: string;
  content: string;
  long: string;
};

export const useTyreTeam = (location: DealerLocation | undefined) => {
  const { t } = useTranslation();
  const { data: tyreDeliveries = [], isFetching: isLoadingTyreDeliveries } = usePermanentTyreTeamDelivery(location);

  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState<string[]>([]);

  useEffect(() => {
    if (tyreDeliveries.length && !selectedDeliveryIds.length) {
      setSelectedDeliveryIds(
        tyreDeliveries.reduce<string[]>((ids, delivery) => {
          if (!delivery.id.includes("PICKUP")) ids.push(delivery.id);
          return ids;
        }, [])
      );
    }
  }, [tyreDeliveries]);

  const getDeliveryChannelName = (channel?: TYRETEAM_CHANNELS | null) => (channel ? deliveryChannels[channel] : t("v8_unknown_channel", "Unknown channel"));

  const getDeliveryPopup = (delivery?: string | null): DeliveryPopup | null => {
    if (!delivery) return null;

    const selectedDelivery = tyreDeliveries.find(d => d.id === delivery);

    const order_before = formatDate(selectedDelivery?.order_before, DATE_FORMATS_FNS.hoursMinutes);
    const delivery_before = formatDate(selectedDelivery?.delivery_before, DATE_FORMATS_FNS.hoursMinutes);
    const delivery_from = formatDate(selectedDelivery?.delivery_from, DATE_FORMATS_FNS.hoursMinutes);

    if (delivery.includes("PICKUPSAMEDAY")) {
      return {
        trigger: t("v8_pickup_sameday", "Pickup sameday"),
        content: t("v8_tyre_team_pickupsameday_detail", "Order before {{order_before}} delivery from {{delivery_from}}", { order_before, delivery_from }),
        long: t("v8_tyre_team_pickup_sameday", "Pickup same day (Order before {{order_before}} delivery from {{delivery_from}})", { order_before, delivery_from })
      };
    }

    if (delivery.includes("SAMEDAY")) {
      return {
        trigger: t("v8_sameday", "Sameday"),
        content: t("v8_tyre_team_sameday_detail", "Order before {{order_before}} delivery before {{delivery_before}}", { order_before, delivery_before }),
        long: t("v8_tyre_team_sameday", "Same day (Order before {{order_before}} delivery before {{delivery_before}})", { order_before, delivery_before })
      };
    }

    if (delivery.includes("PICKUPOVERNIGHT")) {
      return {
        trigger: t("v8_pickup_overnight", "Pickup Overnight"),
        content: t("v8_tyre_team_pickupovernight_detail", "Order before {{order_before}} pickup tomorrow from {{delivery_from}}", { order_before, delivery_from }),
        long: t("v8_tyre_team_pickupovernight", "Pickup overnight (Order before {{order_before}} pickup tomorrow from {{delivery_from}})", {
          order_before,
          delivery_from
        })
      };
    }

    if (delivery.includes("OVERNIGHT")) {
      return {
        trigger: t("v8_overnight", "Overnight"),
        content: t("v8_tyre_team_overnight_detail", "Order before {{order_before}} delivery tomorrow before {{delivery_before}}", { order_before, delivery_before }),
        long: t("v8_tyre_team_overnight", "Overnight (Order before {{order_before}} delivery tomorrow before {{delivery_before}})", { order_before, delivery_before })
      };
    }

    return null;
  };

  const getDeliveriesOptions = () =>
    tyreDeliveries.map(item => ({
      key: item.id,
      value: item.id,
      text: getDeliveryPopup(item.id)?.long ?? ""
    }));

  return {
    selectedDeliveryIds,
    setSelectedDeliveryIds,
    tyreDeliveries,
    isLoadingTyreDeliveries,
    getDeliveriesOptions,
    getDeliveryChannelName,
    getDeliveryPopup
  };
};
